/**
 * Compliance calendar + checklist data access (migration 0006).
 *
 * All queries run client-side under the signed-in user's RLS (build doc ADR-0004
 * MVP pipeline pattern). Reads are member-scoped; writes are role-gated by the
 * DB policies — the UI mirrors those gates for clear messaging only.
 */
import { supabase } from "@/lib/supabase/client";
import { addDays, upcomingPeriods, type ComplianceFrequency } from "@argus/engine";

export type ComplianceCategory =
  | "regulatory_filing"
  | "regulatory_attestation"
  | "lp_report"
  | "lp_contractual"
  | "tax"
  | "internal_policy"
  | "other";

export type ComplianceAuthority =
  | "sec"
  | "irs"
  | "state"
  | "cftc_nfa"
  | "eu"
  | "finra"
  | "internal"
  | "lp"
  | "other";

export type RequirementScope = "firm" | "fund" | "lp";
export type RequirementSource = "regulatory_catalog" | "side_letter" | "manual";
export type TaskStatus = "open" | "in_progress" | "blocked" | "submitted" | "completed" | "waived";
export type ChecklistStatus = "outstanding" | "collected" | "not_applicable";

export interface ChecklistTemplateItem {
  label: string;
  detail?: string;
}

export interface CatalogRow {
  id: string;
  key: string;
  title: string;
  description: string | null;
  category: ComplianceCategory;
  authority: ComplianceAuthority;
  citation: string | null;
  default_frequency: ComplianceFrequency;
  default_lead_time_days: number;
  default_scope: RequirementScope;
  typical_due: string | null;
  checklist_template: ChecklistTemplateItem[];
}

export interface RequirementRow {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  category: ComplianceCategory;
  authority: ComplianceAuthority;
  citation: string | null;
  source: RequirementSource;
  catalog_key: string | null;
  source_provision_id: string | null;
  scope: RequirementScope;
  fund_id: string | null;
  lp_id: string | null;
  frequency: ComplianceFrequency;
  default_lead_time_days: number;
  owner_id: string | null;
  checklist_template: ChecklistTemplateItem[];
  active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface CalendarRow {
  id: string;
  tenant_id: string;
  kind: "task" | "obligation";
  source: RequirementSource;
  category: ComplianceCategory;
  authority: ComplianceAuthority;
  citation: string | null;
  title: string;
  scope: RequirementScope;
  requirement_id: string | null;
  fund_id: string | null;
  lp_id: string | null;
  due_date: string | null;
  lead_time_days: number;
  status: TaskStatus;
  assigned_to: string | null;
  period_label: string | null;
  completed_at: string | null;
}

export interface TaskRow {
  id: string;
  tenant_id: string;
  requirement_id: string;
  title: string;
  period_label: string | null;
  due_date: string;
  lead_time_days: number;
  status: TaskStatus;
  assigned_to: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
}

export interface ChecklistItemRow {
  id: string;
  tenant_id: string;
  task_id: string;
  label: string;
  detail: string | null;
  status: ChecklistStatus;
  evidence_storage_path: string | null;
  collected_at: string | null;
  collected_by: string | null;
  sort_order: number;
}

export interface TaskEventRow {
  id: string;
  task_id: string;
  event_type: string;
  from_status: string | null;
  to_status: string | null;
  note: string | null;
  actor_id: string | null;
  created_at: string;
}

const REQ_COLS =
  "id, tenant_id, title, description, category, authority, citation, source, catalog_key, source_provision_id, scope, fund_id, lp_id, frequency, default_lead_time_days, owner_id, checklist_template, active, metadata, created_at";
const TASK_COLS =
  "id, tenant_id, requirement_id, title, period_label, due_date, lead_time_days, status, assigned_to, submitted_at, completed_at, completed_by, notes, metadata";

/* --------------------------------------------------------------- Catalog */

export async function listCatalog(): Promise<CatalogRow[]> {
  const { data, error } = await supabase
    .from("regulatory_catalog")
    .select(
      "id, key, title, description, category, authority, citation, default_frequency, default_lead_time_days, default_scope, typical_due, checklist_template",
    )
    .eq("active", true)
    .order("authority", { ascending: true })
    .order("title", { ascending: true });
  if (error) throw new Error(`listCatalog: ${error.message}`);
  return data ?? [];
}

/* ---------------------------------------------------------- Requirements */

export async function listRequirements(tenantId: string): Promise<RequirementRow[]> {
  const { data, error } = await supabase
    .from("compliance_requirements")
    .select(REQ_COLS)
    .eq("tenant_id", tenantId)
    .order("active", { ascending: false })
    .order("title", { ascending: true });
  if (error) throw new Error(`listRequirements: ${error.message}`);
  return data ?? [];
}

export async function getRequirement(id: string): Promise<RequirementRow> {
  const { data, error } = await supabase
    .from("compliance_requirements")
    .select(REQ_COLS)
    .eq("id", id)
    .single();
  if (error) throw new Error(`getRequirement: ${error.message}`);
  return data;
}

export interface CreateRequirementInput {
  tenant_id: string;
  title: string;
  description?: string | null;
  category: ComplianceCategory;
  authority?: ComplianceAuthority;
  citation?: string | null;
  source?: RequirementSource;
  catalog_key?: string | null;
  scope?: RequirementScope;
  fund_id?: string | null;
  lp_id?: string | null;
  frequency: ComplianceFrequency;
  default_lead_time_days?: number;
  owner_id?: string | null;
  checklist_template?: ChecklistTemplateItem[];
  metadata?: Record<string, unknown>;
}

export async function createRequirement(input: CreateRequirementInput): Promise<RequirementRow> {
  const { data, error } = await supabase
    .from("compliance_requirements")
    .insert(input)
    .select(REQ_COLS)
    .single();
  if (error) throw new Error(`createRequirement: ${error.message}`);
  return data;
}

/** Instantiate a catalog entry as a tenant requirement (copies template + lead). */
export async function addRequirementFromCatalog(
  tenantId: string,
  catalog: CatalogRow,
  overrides: { fund_id?: string | null; owner_id?: string | null; scope?: RequirementScope } = {},
): Promise<RequirementRow> {
  return createRequirement({
    tenant_id: tenantId,
    title: catalog.title,
    description: catalog.description,
    category: catalog.category,
    authority: catalog.authority,
    citation: catalog.citation,
    source: "regulatory_catalog",
    catalog_key: catalog.key,
    scope: overrides.scope ?? catalog.default_scope,
    fund_id: overrides.fund_id ?? null,
    frequency: catalog.default_frequency,
    default_lead_time_days: catalog.default_lead_time_days,
    owner_id: overrides.owner_id ?? null,
    checklist_template: catalog.checklist_template,
  });
}

export async function updateRequirement(
  id: string,
  patch: Partial<CreateRequirementInput> & { active?: boolean },
): Promise<void> {
  const { error } = await supabase.from("compliance_requirements").update(patch).eq("id", id);
  if (error) throw new Error(`updateRequirement: ${error.message}`);
}

export async function deleteRequirement(id: string): Promise<void> {
  const { error } = await supabase.from("compliance_requirements").delete().eq("id", id);
  if (error) throw new Error(`deleteRequirement: ${error.message}`);
}

/* ----------------------------------------------------------------- Tasks */

export async function listCalendar(tenantId: string): Promise<CalendarRow[]> {
  const { data, error } = await supabase
    .from("v_compliance_calendar")
    .select(
      "id, tenant_id, kind, source, category, authority, citation, title, scope, requirement_id, fund_id, lp_id, due_date, lead_time_days, status, assigned_to, period_label, completed_at",
    )
    .eq("tenant_id", tenantId)
    .order("due_date", { ascending: true, nullsFirst: false });
  if (error) throw new Error(`listCalendar: ${error.message}`);
  return data ?? [];
}

export async function getTask(taskId: string): Promise<TaskRow> {
  const { data, error } = await supabase
    .from("compliance_tasks")
    .select(TASK_COLS)
    .eq("id", taskId)
    .single();
  if (error) throw new Error(`getTask: ${error.message}`);
  return data;
}

export async function listTasksForRequirement(requirementId: string): Promise<TaskRow[]> {
  const { data, error } = await supabase
    .from("compliance_tasks")
    .select(TASK_COLS)
    .eq("requirement_id", requirementId)
    .order("due_date", { ascending: true });
  if (error) throw new Error(`listTasksForRequirement: ${error.message}`);
  return data ?? [];
}

/**
 * Generate the next `count` dated tasks for a recurring requirement, each with a
 * checklist copied from the requirement's template. Idempotent: existing
 * (requirement, period) pairs are skipped via upsert ignore-duplicates.
 * Returns the number of NEW tasks created.
 */
export async function generateUpcomingTasks(
  req: RequirementRow,
  referenceIso: string,
  count = 4,
): Promise<number> {
  if (req.frequency === "one_time" || req.frequency === "on_event") return 0;
  const offset = Number((req.metadata?.filing_offset_days as number | undefined) ?? 0);
  const periods = upcomingPeriods(req.frequency, referenceIso, count);
  if (periods.length === 0) return 0;

  const rows = periods.map((p) => ({
    tenant_id: req.tenant_id,
    requirement_id: req.id,
    title: req.title,
    period_label: p.label,
    due_date: offset ? addDays(p.end, offset) : p.end,
    lead_time_days: req.default_lead_time_days,
    assigned_to: req.owner_id,
  }));

  const { data, error } = await supabase
    .from("compliance_tasks")
    .upsert(rows, { onConflict: "requirement_id,period_label", ignoreDuplicates: true })
    .select("id");
  if (error) throw new Error(`generateUpcomingTasks: ${error.message}`);

  const created = (data ?? []) as { id: string }[];
  if (created.length > 0 && req.checklist_template.length > 0) {
    const items = created.flatMap((t) =>
      req.checklist_template.map((c, i) => ({
        tenant_id: req.tenant_id,
        task_id: t.id,
        label: c.label,
        detail: c.detail ?? null,
        sort_order: i,
      })),
    );
    const { error: ciErr } = await supabase.from("compliance_checklist_items").insert(items);
    if (ciErr) throw new Error(`generateUpcomingTasks/checklist: ${ciErr.message}`);
  }
  return created.length;
}

export async function createOneOffTask(input: {
  tenant_id: string;
  requirement_id: string;
  title: string;
  due_date: string;
  period_label?: string | null;
  lead_time_days?: number;
  assigned_to?: string | null;
}): Promise<TaskRow> {
  const { data, error } = await supabase
    .from("compliance_tasks")
    .insert(input)
    .select(TASK_COLS)
    .single();
  if (error) throw new Error(`createOneOffTask: ${error.message}`);
  return data;
}

const TERMINAL: TaskStatus[] = ["completed", "waived"];

/** Transition a task's status and write the append-only event in one flow. */
export async function setTaskStatus(
  task: TaskRow,
  to: TaskStatus,
  userId: string,
  note?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const patch: Record<string, unknown> = { status: to };
  if (to === "submitted") patch.submitted_at = now;
  if (to === "completed") {
    patch.completed_at = now;
    patch.completed_by = userId;
  }
  if (!TERMINAL.includes(to) && task.completed_at) {
    patch.completed_at = null;
    patch.completed_by = null;
  }
  const { error } = await supabase.from("compliance_tasks").update(patch).eq("id", task.id);
  if (error) throw new Error(`setTaskStatus: ${error.message}`);

  const eventType =
    to === "completed"
      ? "completed"
      : to === "submitted"
        ? "submitted"
        : task.status === "completed"
          ? "reopened"
          : "status_change";
  const { error: evErr } = await supabase.from("compliance_task_events").insert({
    tenant_id: task.tenant_id,
    task_id: task.id,
    event_type: eventType,
    from_status: task.status,
    to_status: to,
    note: note ?? null,
    actor_id: userId,
  });
  if (evErr) throw new Error(`setTaskStatus/event: ${evErr.message}`);
}

export async function assignTask(taskId: string, userId: string | null): Promise<void> {
  const { error } = await supabase
    .from("compliance_tasks")
    .update({ assigned_to: userId })
    .eq("id", taskId);
  if (error) throw new Error(`assignTask: ${error.message}`);
}

/* ------------------------------------------------------------- Checklist */

export async function listChecklistItems(taskId: string): Promise<ChecklistItemRow[]> {
  const { data, error } = await supabase
    .from("compliance_checklist_items")
    .select(
      "id, tenant_id, task_id, label, detail, status, evidence_storage_path, collected_at, collected_by, sort_order",
    )
    .eq("task_id", taskId)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(`listChecklistItems: ${error.message}`);
  return data ?? [];
}

export async function setChecklistItemStatus(
  item: ChecklistItemRow,
  status: ChecklistStatus,
  userId: string,
): Promise<void> {
  const patch: Record<string, unknown> = {
    status,
    collected_at: status === "collected" ? new Date().toISOString() : null,
    collected_by: status === "collected" ? userId : null,
  };
  const { error } = await supabase
    .from("compliance_checklist_items")
    .update(patch)
    .eq("id", item.id);
  if (error) throw new Error(`setChecklistItemStatus: ${error.message}`);
}

export async function addChecklistItem(
  tenantId: string,
  taskId: string,
  label: string,
  sortOrder: number,
): Promise<void> {
  const { error } = await supabase
    .from("compliance_checklist_items")
    .insert({ tenant_id: tenantId, task_id: taskId, label, sort_order: sortOrder });
  if (error) throw new Error(`addChecklistItem: ${error.message}`);
}

/* -------------------------------------------------- Deadline-alert digest */

export interface DigestResult {
  configured: boolean;
  sent?: boolean;
  to?: string;
  reason?: string;
  overdue: number;
  dueSoon: number;
  preview?: { subject: string; html: string };
}

/**
 * Invoke the compliance-digest edge function. When RESEND_API_KEY is not set
 * server-side, the function returns 501 with a rendered preview so the UI can
 * show the email unsent (deploy-ready, no-op-safe — same pattern as extraction).
 */
interface DigestResponse {
  sent?: boolean;
  to?: string;
  reason?: string;
  overdue: number;
  dueSoon: number;
}

export async function sendComplianceDigest(tenantId: string): Promise<DigestResult> {
  const { data, error } = (await supabase.functions.invoke<DigestResponse>("compliance-digest", {
    body: { tenantId },
  })) as { data: DigestResponse | null; error: { message: string; context?: Response } | null };
  if (error) {
    // Deployed-but-unconfigured (501): the function returns a rendered preview.
    const ctx = error.context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const body = (await ctx.json()) as {
          preview?: { subject: string; html: string; overdue: number; dueSoon: number };
        };
        if (body.preview) {
          return {
            configured: false,
            overdue: body.preview.overdue,
            dueSoon: body.preview.dueSoon,
            preview: { subject: body.preview.subject, html: body.preview.html },
          };
        }
      } catch {
        /* fall through to the in-browser fallback */
      }
    }
    // Not deployed / unreachable: render the digest preview in-browser so the
    // feature still works (mirrors the extraction + filing-draft fallbacks).
    return buildDigestPreview(tenantId);
  }
  const d: DigestResponse = data ?? { overdue: 0, dueSoon: 0 };
  const result: DigestResult = { configured: true, overdue: d.overdue, dueSoon: d.dueSoon };
  if (d.sent !== undefined) result.sent = d.sent;
  if (d.to !== undefined) result.to = d.to;
  if (d.reason !== undefined) result.reason = d.reason;
  return result;
}

const DIGEST_TERMINAL = new Set<TaskStatus>(["completed", "waived"]);

/** Client-side digest preview, computed from the calendar under the caller's RLS.
 * Matches the compliance-digest edge function's grouping + HTML so the preview is
 * identical whether it comes from the server or the browser. */
async function buildDigestPreview(tenantId: string): Promise<DigestResult> {
  const [rows, firm] = await Promise.all([listCalendar(tenantId), fetchFirmName(tenantId)]);
  const today = new Date().toISOString().slice(0, 10);
  const overdue: CalendarRow[] = [];
  const dueSoon: CalendarRow[] = [];
  for (const r of rows) {
    if (DIGEST_TERMINAL.has(r.status) || !r.due_date) continue;
    if (r.due_date < today) {
      overdue.push(r);
    } else {
      const leadStart = addDays(r.due_date, -r.lead_time_days);
      if (today >= leadStart) dueSoon.push(r);
    }
  }
  const byDue = (a: CalendarRow, b: CalendarRow) =>
    (a.due_date ?? "") < (b.due_date ?? "") ? -1 : 1;
  overdue.sort(byDue);
  dueSoon.sort(byDue);

  const subject = `${firm}: ${overdue.length} overdue, ${dueSoon.length} due soon`;
  const html = renderDigestHtml(firm, overdue, dueSoon);
  return {
    configured: false,
    overdue: overdue.length,
    dueSoon: dueSoon.length,
    preview: { subject, html },
  };
}

async function fetchFirmName(tenantId: string): Promise<string> {
  const { data } = await supabase
    .from("tenants")
    .select("name")
    .eq("id", tenantId)
    .maybeSingle<{ name: string }>();
  return data?.name ?? "Your firm";
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

function renderDigestHtml(firm: string, overdue: CalendarRow[], dueSoon: CalendarRow[]): string {
  const rowHtml = (r: CalendarRow) => {
    const cite = r.citation ? ` &middot; ${escapeHtml(r.citation)}` : "";
    const period = r.period_label ? ` (${escapeHtml(r.period_label)})` : "";
    return `<tr>
      <td style="padding:6px 10px;font-variant-numeric:tabular-nums;white-space:nowrap;color:#334155">${r.due_date}</td>
      <td style="padding:6px 10px;color:#0f172a"><strong>${escapeHtml(r.title)}</strong>${period}<br>
        <span style="font-size:12px;color:#64748b">${r.authority.toUpperCase()} &middot; ${r.category.replace(/_/g, " ")}${cite}</span></td>
    </tr>`;
  };
  const section = (heading: string, color: string, rows: CalendarRow[]) =>
    rows.length === 0
      ? ""
      : `<h2 style="font:600 14px system-ui;color:${color};margin:18px 0 6px">${heading} (${rows.length})</h2>
         <table style="border-collapse:collapse;width:100%;font:14px system-ui">${rows.map(rowHtml).join("")}</table>`;
  return `<div style="max-width:640px;margin:0 auto;font:14px system-ui;color:#0f172a">
    <p style="font:600 16px system-ui">${escapeHtml(firm)} — compliance deadlines</p>
    <p style="color:#64748b;font-size:13px">Items overdue or entering their lead-time window. Open Argus to manage owners and checklists.</p>
    ${section("Overdue", "#b91c1c", overdue)}
    ${section("Due soon", "#b45309", dueSoon)}
    <p style="margin-top:20px;font-size:12px;color:#94a3b8">Sent by Argus &middot; Henosis. This is a compliance reminder, not legal advice.</p>
  </div>`;
}

export async function listTaskEvents(taskId: string): Promise<TaskEventRow[]> {
  const { data, error } = await supabase
    .from("compliance_task_events")
    .select("id, task_id, event_type, from_status, to_status, note, actor_id, created_at")
    .eq("task_id", taskId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(`listTaskEvents: ${error.message}`);
  return data ?? [];
}
